import { format } from 'date-fns';
import { createAppointment, updateAppointment, cancelAppointment, CreateAppointmentPayload } from '@/lib/api/appointments';
import { buildAppointmentUtcTimes, getAppointmentSchedulingTimezone } from '@/utils/appointments/appointment-timezone';
import { extractErrorMessage, showSuccessToast, showErrorToast } from '@/utils/toast-utils';
import { Appointment } from '@/types';
import { AppointmentFormValues } from '@/components/appointments/AppointmentForm';

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

const dayKeyToNumber: Record<DayKey, number> = {
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
  sun: 0,
};

const buildRecurrencePattern = (recurrence: AppointmentFormValues['recurrence'], startTime: Date) => {
  if (!recurrence || recurrence.frequency === 'none') {
    return undefined;
  }

  const endDate = recurrence.endDate ? new Date(recurrence.endDate) : undefined;
  const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;

  // Base pattern without endDate
  const basePattern = {
    interval: 1,
  };

  if (recurrence.frequency === 'daily') {
    return {
      type: 'daily' as const,
      ...basePattern,
      // Only include endDate if it exists (omit for "forever" recurring)
      ...(formattedEndDate && { endDate: formattedEndDate }),
    };
  }

  if (recurrence.frequency === 'weekly') {
    // Map day keys to numbers (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    // No timezone adjustment needed since we send date/time/timezone separately
    const daysOfWeek = (recurrence.weeklyDays || []).map((d) => dayKeyToNumber[d as DayKey]);

    return {
      type: 'weekly' as const,
      ...basePattern,
      daysOfWeek,
      // Only include endDate if it exists (omit for "forever" recurring)
      ...(formattedEndDate && { endDate: formattedEndDate }),
    };
  }

  const daysOfMonth = (recurrence.monthlyDates || []).filter((n) => n >= 1 && n <= 31);
  return {
    type: 'monthly' as const,
    ...basePattern,
    daysOfMonth,
    // Only include endDate if it exists (omit for "forever" recurring)
    ...(formattedEndDate && { endDate: formattedEndDate }),
  };
};

type AppointmentActionOptions = {
  onError?: (title: string, message: string) => void;
  suppressSuccessToast?: boolean;
};

export const createAppointmentAction = async (
  data: AppointmentFormValues,
  selectedAppointment: Appointment | null,
  updateCreateDialogState: (open: boolean) => void,
  setSelectedAppointment: (appointment: Appointment | null) => void,
  setSelectedTime: (time: string | undefined) => void,
  fetchAppointments: () => Promise<void>,
  updateScope?: 'this_only' | 'this_and_following' | 'all',
  options?: AppointmentActionOptions,
): Promise<void> => {
  try {
    // Format date as YYYY-MM-DD
    const formattedDate = format(data.date, 'yyyy-MM-dd');

    // Use time strings directly (already in HH:mm format)
    const startTimeStr = data.time;
    const endTimeStr = data.endTime;

    // Create Date object for recurrence pattern building
    const [startHours, startMinutes] = startTimeStr.split(':').map(Number);
    const startTimeDate = new Date(data.date);
    startTimeDate.setHours(startHours, startMinutes, 0, 0);

    // Process additional attendees
    const additionalAttendees = data.additionalAttendees
      ? data.additionalAttendees.split(',').map(email => email.trim()).filter(email => email)
      : [];

    const schedulingTimezone = data.timezone?.trim() || getAppointmentSchedulingTimezone();
    const utcTimes = buildAppointmentUtcTimes({
      date: formattedDate,
      startTime: startTimeStr,
      endTime: endTimeStr,
      timezone: schedulingTimezone,
    });

    if (selectedAppointment) {
      // Update existing appointment
      const finalUpdateScope = updateScope || 'this_only';

      const updatePayload = {
        clientId: data.clientId, // Include updated client ID
        updateScope: finalUpdateScope,
        title: data.title,
        startTime: utcTimes.startTime,
        endTime: utcTimes.endTime,
        description: data.description,
        appointmentType: (data.createMeetLink === false) ? 'offline' as const : 'online' as const,
        location: (data.createMeetLink === false) ? 'In Person' : 'Video Call',
        additionalAttendees,
        // Include recurrencePattern if it's being changed (e.g. when updateScope is 'all' or simplified check)
        // For 'all', we definitely want to update the pattern if changed
        ...(finalUpdateScope === 'all' && { recurrencePattern: buildRecurrencePattern(data.recurrence, startTimeDate) }),
      };

      await updateAppointment(selectedAppointment.id, updatePayload);

      if (!options?.suppressSuccessToast) {
        showSuccessToast(
          'Appointment updated',
          'The appointment has been successfully rescheduled/updated.'
        );
      }
    } else {
      const createPayload: CreateAppointmentPayload = {
        clientId: data.clientId,
        title: data.title,
        description: data.description,
        startTime: utcTimes.startTime,
        endTime: utcTimes.endTime,
        additionalAttendees,
        appointmentType: (data.createMeetLink === false) ? 'offline' : 'online',
        location: (data.createMeetLink === false) ? 'In Person' : 'Video Call',
        recurrencePattern: buildRecurrencePattern(data.recurrence, startTimeDate),
      };

      await createAppointment(createPayload);

      if (!options?.suppressSuccessToast) {
        showSuccessToast(
          'Appointment created',
          'The appointment has been successfully scheduled.'
        );
      }
    }

    // Close the dialog and clear selected time
    updateCreateDialogState(false);
    setSelectedAppointment(null);
    setSelectedTime(undefined);

    // Refresh appointments list
    await fetchAppointments();
  } catch (error: any) {
    console.error('Error saving appointment:', error);
    const title = selectedAppointment ? 'Failed to update appointment' : 'Failed to create appointment';
    if (options?.onError) {
      options.onError(title, extractErrorMessage(error));
    } else {
      showErrorToast(error, title);
    }
  }
};

export const deleteAppointmentAction = async (
  selectedAppointment: Appointment | null,
  setAppointmentDetailOpen: (open: boolean) => void,
  setSelectedAppointment: (appointment: Appointment | null) => void,
  fetchAppointments: () => Promise<void>,
  updateScope?: 'this_only' | 'this_and_following' | 'all',
  options?: AppointmentActionOptions,
) => {
  if (selectedAppointment) {
    try {
      // Cancel the appointment using the API with updateScope
      const finalUpdateScope = updateScope || 'this_only';
      await cancelAppointment(selectedAppointment.id, finalUpdateScope);

      // Show success toast with appropriate message based on scope
      const scopeMessage =
        finalUpdateScope === 'this_only'
          ? 'This appointment instance has been cancelled.'
          : finalUpdateScope === 'this_and_following'
            ? 'This appointment and all following instances have been cancelled.'
            : 'All appointments in the series have been cancelled.';

      if (!options?.suppressSuccessToast) {
        showSuccessToast('Appointment cancelled', scopeMessage);
      }

      // Close modal and clear selection
      setAppointmentDetailOpen(false);
      setSelectedAppointment(null);

      // Refresh appointments list
      await fetchAppointments();
      return true;
    } catch (error: any) {
      console.error('Error cancelling appointment:', error);
      if (options?.onError) {
        options.onError('Failed to cancel appointment', extractErrorMessage(error));
      } else {
        showErrorToast(error, 'Failed to cancel appointment');
      }
      return false;
    }
  }
  return false;
};

export const editAppointmentAction = (
  selectedAppointment: Appointment | null,
  setAppointmentDetailOpen: (open: boolean) => void,
  updateCreateDialogState: (open: boolean) => void
) => {
  if (selectedAppointment) {
    // Close details dialog
    setAppointmentDetailOpen(false);
    // Open edit dialog directly (scope dialog will be shown on save for recurring appointments)
    updateCreateDialogState(true);
  }
};
